import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CalendarDays, Users, Database, ArrowUpDown } from 'lucide-react';
import { DashboardShell } from '../layout/DashboardShell';
import { UserMenu } from '../components/UserMenu';
import { useEvents } from '../context/EventsContext';
import { useRealProfiles } from '../hooks/useRealProfiles';
import { EventCard } from './EventCard';
import { DashPanel } from '../components/DashPanel';
import { MetricStat } from '../components/MetricStat';
import { QuotaUsagePanel } from '../components/QuotaUsagePanel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { eventDateRange, filterProfiles, indexOperatorsByRoomId, matchesViewMode } from '../utils/profileFilter';
import { computeRealGeneralAnalytics } from '../utils/realGeneralAnalytics';

type EventSort = 'date-desc' | 'date-asc' | 'created-desc' | 'created-asc';

const SORT_OPTIONS: { id: EventSort; label: string }[] = [
  { id: 'date-desc', label: 'Data do evento (mais recente)' },
  { id: 'date-asc', label: 'Data do evento (mais antiga)' },
  { id: 'created-desc', label: 'Criação (mais recente)' },
  { id: 'created-asc', label: 'Criação (mais antiga)' },
];

export function DashboardHome() {
  const { events } = useEvents();
  const { profiles: realProfiles, operators } = useRealProfiles();
  const navigate = useNavigate();
  const [sort, setSort] = useState<EventSort>('date-desc');

  // Calculado ao vivo (mesmo princípio de AnalysisDetail.tsx/"Análise
  // cruzada" — união dos perfis de cada evento, deduplicada por pessoa, não
  // soma ingênua) — não usa event.participantCount/recordCount (escritos só
  // quando alguém abre aquele evento específico, até 10min desatualizados
  // dali, e sem deduplicar gente que aparece em mais de um evento). A soma
  // ingênua podia tanto sub quanto super-contar, e nunca batia com os
  // totais "ao vivo" mostrados na Análise cruzada.
  const totals = useMemo(() => {
    const seen = new Set<string>();
    const union: typeof realProfiles = [];
    for (const ev of events) {
      const range = eventDateRange(Boolean(ev.noPeriod), ev.startDate, ev.endDate);
      const evProfiles = filterProfiles(realProfiles, 'todos', { dateRange: range, operators });
      for (const p of evProfiles) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          union.push(p);
        }
      }
    }
    const operatorsByRoomId = indexOperatorsByRoomId(operators);
    let managed = 0;
    let professor = 0;
    let paiMae = 0;
    for (const p of union) {
      if (matchesViewMode(p, 'managed', operatorsByRoomId)) managed++;
      else if (matchesViewMode(p, 'professor', operatorsByRoomId)) professor++;
      else if (matchesViewMode(p, 'pai_mae', operatorsByRoomId)) paiMae++;
    }
    return {
      participants: union.length,
      managed,
      professor,
      paiMae,
      unclassified: union.length - (managed + professor + paiMae),
      records: computeRealGeneralAnalytics(union).totalRecords,
    };
  }, [events, realProfiles, operators]);

  // startDate ("AAAA-MM-DD") e createdAt (ISO) são strings lexicograficamente
  // ordenáveis — sem precisar converter pra Date pra comparar.
  const sortedEvents = useMemo(() => {
    const copy = [...events];
    copy.sort((a, b) => {
      switch (sort) {
        case 'date-desc':
          return b.startDate.localeCompare(a.startDate);
        case 'date-asc':
          return a.startDate.localeCompare(b.startDate);
        case 'created-desc':
          return b.createdAt.localeCompare(a.createdAt);
        case 'created-asc':
          return a.createdAt.localeCompare(b.createdAt);
        default:
          return 0;
      }
    });
    return copy;
  }, [events, sort]);

  return (
    <DashboardShell mode="home">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-foreground">Eventos de pesquisa</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Organize coletas, compare resultados e gere evidências científicas
          </p>
        </div>
        <UserMenu />
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-blue-100 dark:bg-background border-t border-l border-border rounded-tl-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <DashPanel padding="sm">
            <MetricStat icon={CalendarDays} label="Eventos criados" value={events.length} />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat icon={Users} label="Participantes" value={totals.participants} />
            <p className="mt-2 text-[11px] text-muted-foreground">
              Alunos {totals.managed} · Professores {totals.professor} · Pai/Mãe {totals.paiMae}
              {totals.unclassified > 0 && ` · ${totals.unclassified} sem papel definido`}
            </p>
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat icon={Database} label="Registros totais" value={totals.records} />
          </DashPanel>
        </div>

        <QuotaUsagePanel />

        <section>
          <div className="flex items-center justify-between mb-4 gap-3">
            <h2 className="text-sm font-semibold text-foreground">Seus eventos</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {events.length} evento{events.length === 1 ? '' : 's'}
              </span>
              <Select value={sort} onValueChange={(v) => setSort(v as EventSort)}>
                <SelectTrigger className="h-8 w-[230px] text-xs">
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/events/new')}
              className="text-left h-full min-h-[280px]"
            >
              <DashPanel
                padding="sm"
                className="h-full flex flex-col items-center justify-center border-dashed hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground">Criar evento</p>
                <p className="text-xs text-muted-foreground text-center mt-1">
                  Nova coleta com intervalo temporal
                </p>
              </DashPanel>
            </button>
            {sortedEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      </div>
    </DashboardShell>
  );
}
